// Disable HMR for this plugin to prevent conflicts
import { defineNuxtPlugin, useRouter, useRuntimeConfig } from 'nuxt/app'
import { useStore } from 'vuex'
import Intercom from '@intercom/messenger-js-sdk'

if (import.meta.hot) {
  import.meta.hot.decline()
}

// Make sure that pageviews are captured with each route change
export default defineNuxtPlugin((nuxtApp) => {
  // Skip in test environment or SSR
  if (import.meta.env.MODE === 'test' || import.meta.server) {
    return
  }

  const router = useRouter()
  const store = useStore()
  const config = useRuntimeConfig()

  // Determine if user is logged in
  const isLoggedIn = (auth) => {
    if (!auth || !auth.account || !auth.account.promoterOid) {
      return false
    }

    return true
  }

  // Generate JWT token for Intercom user verification via API endpoint
  const generateIntercomJWT = async (userProperties) => {
    try {
      const response = await nuxtApp.$fetch('/api/intercom-jwt', {
        method: 'POST',
        body: {
          oid: userProperties.oid,
          emailAddress: userProperties.emailAddress,
          companyName: userProperties.companyName,
          promoterOid: userProperties.promoterOid,
          firstName: userProperties.firstName,
          lastName: userProperties.lastName,
        },
      })
      return response.jwt
    }
    catch (error) {
      console.warn('Failed to generate Intercom JWT:', error)
      return null
    }
  }

  // Get and set properties of logged in user (i.e company name, email address etc)
  // It allows us to add user properties to Intercom
  const setUserProperties = (auth) => {
    const account = auth?.account || {}
    return {
      oid: account.oid || '',
      promoterOid: account.promoterOid || '',
      companyName: account.companyName || '',
      emailAddress: account.emailAddress || '',
      firstName: account.firstName || '',
      lastName: account.lastName || '',
    }
  }

  // Make sure that pageviews are captured with each route change
  router.afterEach(async (_to) => {
    const auth = store?.state?.auth

    // Ensure user is logged in
    if (!isLoggedIn(auth))
      return

    const userProperties = setUserProperties(auth)

    // Generate JWT token via API endpoint
    const jwt = await generateIntercomJWT(userProperties)

    // Skip Intercom initialization if JWT generation failed
    if (!jwt) {
      console.warn('Intercom disabled: JWT generation failed')
      return
    }

    // Get app ID from runtime config
    const appId = config.public.arIntercomFinAppId

    if (!appId) {
      console.warn('Intercom disabled: No app ID configured')
      return
    }

    Intercom({
      api_base: 'https://api-iam.intercom.io',
      app_id: appId,
      intercom_user_jwt: jwt,
      user_id: userProperties.oid,
      name: `${userProperties.firstName} ${userProperties.lastName}`,
      email: userProperties.emailAddress,
      company: {
        company_id: userProperties.promoterOid,
        name: userProperties.companyName,
      },
    })
  })
})
